export const compatMap = {
  MERCEDES_BENZ: 'Mercedes-Benz',
  MERCEDES_AMG: 'Mercedes-AMG',
  MERCEDES_MAYBACH: 'Mercedes-Maybach',
  get 'Mercedes-Benz'() {
    return compatMap.MERCEDES_BENZ;
  },
  get 'Mercedes-AMG'() {
    return compatMap.MERCEDES_AMG;
  },
  get 'Mercedes-Maybach'() {
    return compatMap.MERCEDES_MAYBACH;
  },
  get amg() {
    return compatMap.MERCEDES_AMG;
  },
  get maybach() {
    return compatMap.MERCEDES_MAYBACH;
  },
} as const;

const electricFuelTypes = ['ELECTRIC', 'HYBRID'];
const petrolDieselFuelTypes = ['PETROL', 'DIESEL'];
export const vehicleAssortmentPrivate = 'new-passenger-cars';
export const vehicleAssortmentCommercial = 'new-commercial-vans';
export const vehicleCategoryPrivate = 'PASSENGER-CARS';
export const vehicleCategoryCommercial = 'VANS';
export type VehicleCategory = typeof vehicleCategoryPrivate | typeof vehicleCategoryCommercial;

type CompatMap = {
  [string: string]: {
    identifier: string;
    fuelType: string[];
    body: string | null;
    assortment: string | null;
    vehicleCategory: string | null;
    clusteringCriteria?: string[];
  };
};

export const COMMERCIAL_VANS = [
  'CITAN',
  'CITAN_PANEL_VAN',
  'CITAN-PANEL_VAN',
  'CITAN_MIXTO',
  'CITAN-MIXTO',
  'CITAN_TOURER',
  'CITAN-TOURER',
  'eCITAN',
  'eCITAN_PANEL_VAN',
  'eCITAN-PANEL_VAN',
  'eCITAN_TOURER',
  'eCITAN-TOURER',
  'SPRINTER',
  'SPRINTER_CHASSIS',
  'SPRINTER-CHASSIS',
  'SPRINTER_PLATFORM',
  'SPRINTER-PLATFORM',
  'SPRINTER_PANEL_VAN',
  'SPRINTER-PANEL_VAN',
  'SPRINTER_TOURER',
  'SPRINTER-TOURER',
  'eSPRINTER',
  'eSPRINTER_PLATFORM',
  'eSPRINTER-PLATFORM',
  'eSPRINTER_PANEL_VAN',
  'eSPRINTER-PANEL_VAN',
  'VITO',
  'VITO_PANEL_VAN',
  'VITO-PANEL_VAN',
  'VITO_MIXTO',
  'VITO-MIXTO',
  'VITO_TOURER',
  'VITO-TOURER',
  'VITO_PEOPLE_CARRIER',
  'VITO-PEOPLE_CARRIER',
  'eVITO',
  'eVITO_PANEL_VAN',
  'eVITO-PANEL_VAN',
  'eVITO_TOURER',
  'eVITO-TOURER',
];

const PRIVATE_VANS = ['T_CLASS', 'V_CLASS', 'MARCO_POLO', 'EQT_CLASS', 'EQV_CLASS'];

const ALL_VANS = [...COMMERCIAL_VANS, ...PRIVATE_VANS];

/** @description Map of van model identifiers to DLS-compatible values.
 */
export const vanEnumsCompatMap: CompatMap = {
  // electric
  // private
  EQT_CLASS: {
    identifier: 'EQT_CLASS',
    fuelType: electricFuelTypes,
    body: null,
    assortment: vehicleAssortmentPrivate,
    vehicleCategory: vehicleCategoryPrivate,
  },
  EQV_CLASS: {
    identifier: 'EQV_CLASS',
    fuelType: electricFuelTypes,
    body: null,
    assortment: vehicleAssortmentPrivate,
    vehicleCategory: vehicleCategoryPrivate,
  },
  // commercial
  eCITAN: {
    identifier: 'CITAN',
    fuelType: electricFuelTypes,
    body: null,
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  eCITAN_PANEL_VAN: {
    identifier: 'CITAN',
    fuelType: electricFuelTypes,
    body: 'PANEL_VAN',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  eCITAN_TOURER: {
    identifier: 'CITAN',
    fuelType: electricFuelTypes,
    body: 'TOURER',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  eSPRINTER: {
    identifier: 'SPRINTER',
    fuelType: electricFuelTypes,
    body: null,
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  eSPRINTER_PANEL_VAN: {
    identifier: 'SPRINTER',
    fuelType: electricFuelTypes,
    body: 'PANEL_VAN',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  eSPRINTER_PLATFORM: {
    identifier: 'SPRINTER',
    fuelType: electricFuelTypes,
    body: 'PLATFORM',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  eVITO: {
    identifier: 'VITO',
    fuelType: electricFuelTypes,
    body: null,
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  eVITO_PANEL_VAN: {
    identifier: 'VITO',
    fuelType: electricFuelTypes,
    body: 'PANEL_VAN',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  eVITO_TOURER: {
    identifier: 'VITO',
    fuelType: electricFuelTypes,
    body: 'TOURER',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },

  // petrol and diesel
  // private
  T_CLASS: {
    identifier: 'T_CLASS',
    fuelType: petrolDieselFuelTypes,
    body: null,
    assortment: vehicleAssortmentPrivate,
    vehicleCategory: vehicleCategoryCommercial,
  },
  V_CLASS: {
    identifier: 'V_CLASS',
    fuelType: petrolDieselFuelTypes,
    body: null,
    assortment: vehicleAssortmentPrivate,
    vehicleCategory: vehicleCategoryCommercial,
  },
  MARCO_POLO: {
    identifier: 'MARCO_POLO',
    fuelType: petrolDieselFuelTypes,
    body: null,
    assortment: vehicleAssortmentPrivate,
    vehicleCategory: vehicleCategoryCommercial,
  },

  // commercial
  CITAN: {
    identifier: 'CITAN',
    fuelType: petrolDieselFuelTypes,
    body: null,
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  CITAN_PANEL_VAN: {
    identifier: 'CITAN',
    fuelType: petrolDieselFuelTypes,
    body: 'PANEL_VAN',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  CITAN_MIXTO: {
    identifier: 'CITAN',
    fuelType: petrolDieselFuelTypes,
    body: 'MIXTO',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  CITAN_TOURER: {
    identifier: 'CITAN',
    fuelType: petrolDieselFuelTypes,
    body: 'TOURER',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },

  SPRINTER: {
    identifier: 'SPRINTER',
    fuelType: petrolDieselFuelTypes,
    body: null,
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  SPRINTER_CHASSIS: {
    identifier: 'SPRINTER',
    fuelType: petrolDieselFuelTypes,
    body: 'CHASSIS,CHASSIS_CREW_CAB',
    assortment: vehicleAssortmentCommercial,
    clusteringCriteria: ['PRICE', 'BODY_TYPE'],
    vehicleCategory: vehicleCategoryCommercial,
  },
  SPRINTER_PLATFORM: {
    identifier: 'SPRINTER',
    fuelType: petrolDieselFuelTypes,
    body: 'PLATFORM',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  SPRINTER_PANEL_VAN: {
    identifier: 'SPRINTER',
    fuelType: petrolDieselFuelTypes,
    body: 'PANEL_VAN',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  SPRINTER_TOURER: {
    identifier: 'SPRINTER',
    fuelType: petrolDieselFuelTypes,
    body: 'TOURER',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },

  VITO: {
    identifier: 'VITO',
    fuelType: petrolDieselFuelTypes,
    body: null,
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  VITO_PANEL_VAN: {
    identifier: 'VITO',
    fuelType: petrolDieselFuelTypes,
    body: 'PANEL_VAN',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  VITO_MIXTO: {
    identifier: 'VITO',
    fuelType: petrolDieselFuelTypes,
    body: 'MIXTO',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  VITO_TOURER: {
    identifier: 'VITO',
    fuelType: petrolDieselFuelTypes,
    body: 'TOURER',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  VITO_PEOPLE_CARRIER: {
    identifier: 'VITO',
    fuelType: petrolDieselFuelTypes,
    body: 'PEOPLE_CARRIER',
    assortment: vehicleAssortmentCommercial,
    vehicleCategory: vehicleCategoryCommercial,
  },
  DISABLED: { identifier: 'DISABLED', fuelType: [], body: null, assortment: null, vehicleCategory: null },
  ALL: { identifier: ALL_VANS.join(','), fuelType: [], body: null, assortment: null, vehicleCategory: null },
};

export function formatVanKeys(vanKey: string): string {
  const lowerCaseName = vanKey.toLowerCase();
  switch (lowerCaseName) {
    case 'sprinter':
      return 'SPRINTER';
    case 'vito':
      return 'VITO';
    case 'citan':
      return 'CITAN';
    case 'ecitan':
      return 'eCITAN';
    case 'esprinter':
      return 'eSPRINTER';
    case 'evito':
      return 'eVITO';
    default:
      return vanKey;
  }
}
